import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import SideModal from "../SideModal/SideModal";
import { instrument } from "../../constants/instruments";
import InstructorService from "../../services/instructor.service";
import { IoIosAddCircle, IoIosRemoveCircle } from "react-icons/io";
import { countries, foreignCountries } from "../../utilis/country.utiils";
import { Typography } from "@mui/material";

interface IEditCourseModal {
  courseId: any;
  setCourseId: (id: string | undefined) => void;
}

export default function EditCourseModal({ courseId, setCourseId }: IEditCourseModal) {
  const [loading, setLoading] = useState<boolean>(false);
  const [course, setCourse] = useState<any>({
    title: "",
    instrument: "",
    price_per_class: "",
    isVisible: false,
    deleted: false,
    approve: false,
    discount: [],
    international_discounts: [],
  });
  const [tab, setTab] = useState("india");

  useEffect(() => {
    if (courseId) {
      getCourse();
    }
  }, [courseId]);

  const getCourse = async () => {
    try {
      const res = await InstructorService.getCourse(courseId);
      if (res.status === 200) {
        setCourse(res.data.userData);
      }
    } catch (error) {
      toast.error("Failed to fetch course details");
    }
  };

  const editCourse = async () => {
    setLoading(true);
    try {
      const { title, instrument, price_per_class, isVisible, deleted, approve, international_discounts, discount } = course;
      const payload = {
        title,
        instrument,
        price_per_class,
        isVisible,
        deleted,
        approve,
        discount: discount?.map((data: any) => {
          return {
            discount: data?.discount,
            num_classes: data?.num_classes,
          }
        }),
        international_discounts: international_discounts?.map((data: any) => {
          return {
            country: data?.country,
            discount: data?.discount?.map((data: any) => {
              return {
                discount: data?.discount,
                num_classes: data?.num_classes,
              }
            })
          }
        })
      }
      const response = await InstructorService.editCourse(courseId, payload);
      if (response.status === 200) {
        toast.success("Course Updated");
        setCourseId(undefined);
      }
    } catch (error: any) {
      toast.error(error.response?.data || "Something Went Wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChanges = (key: string, value: any) => {
    setCourse((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleDiscountChange = (countryIndex: number, discountIndex: number, key: string, value: any) => {
    setCourse((prev: any) => {
      const updatedDiscounts = [...prev.international_discounts];
      updatedDiscounts[countryIndex] = {
        ...updatedDiscounts[countryIndex],
        discount: updatedDiscounts[countryIndex].discount.map((d: any, i: number) =>
          i === discountIndex ? { ...d, [key]: value } : d
        ),
      };
      return { ...prev, international_discounts: updatedDiscounts };
    });
  };

  const handleCountryChange = (countryIndex: number, selected: any) => {
    setCourse((prev: any) => {
      const updatedDiscounts = [...prev.international_discounts];
      updatedDiscounts[countryIndex].country = selected.value;
      return { ...prev, international_discounts: updatedDiscounts };
    });
  };

  const addDiscount = () => {
    setCourse((prev: any) => ({
      ...prev,
      international_discounts: [...prev.international_discounts, { country: "", discount: [{ num_classes: "0", discount: "0" }] }],
    }));
  };

  const removeDiscount = (countryIndex: number) => {
    setCourse((prev: any) => {
      const updatedDiscounts = prev.international_discounts.filter((_: any, i: number) => i !== countryIndex);
      return { ...prev, international_discounts: updatedDiscounts };
    });
  };

  const removeCountryDiscount = (countryIndex: number, discountIndex: number) => {
    setCourse((prev: any) => {
      const updatedDiscounts = [...prev.international_discounts];
      updatedDiscounts[countryIndex].discount = updatedDiscounts[countryIndex].discount.filter((_: any, i: number) => i !== discountIndex);
      return { ...prev, international_discounts: updatedDiscounts };
    });
  };

  const addCountryDiscount = (countryIndex: number) => {
    setCourse((prev: any) => {
      const updatedDiscounts = [...prev.international_discounts];
      updatedDiscounts[countryIndex].discount.push({ num_classes: "0", discount: "0" });
      return { ...prev, international_discounts: updatedDiscounts };
    });
  };

  const handleRemoveIndiaDiscount = (index: any) => {
    setCourse((prev: any) => {
      const updatedDiscounts = prev.discount.filter((_: any, i: number) => i !== index);
      return { ...prev, discount: updatedDiscounts };
    });
  }

  const addIndiaDiscount = () => {
    setCourse((prev: any) => ({
      ...prev,
      discount: [...prev.discount, { num_classes: "0", discount: "0" }],
    }));
  };

  const handleChangeIndiaDiscount = (index: any, value: any, key: any) => {
    setCourse((prev: any) => {
      const updatedDiscounts = [...prev.discount];
      updatedDiscounts[index] = { ...updatedDiscounts[index], [key]: value };
      return { ...prev, discount: updatedDiscounts };
    });
  };

  return (
    <Modal
      show={!!courseId}
      onHide={() => setCourseId(undefined)}
      size="lg"
      centered={true}
      backdrop="static"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1050,
        display: 'block',
        width: 'auto',
        maxWidth: '90vw',
        minWidth: '50vw',
        maxHeight: '90vh',
        minHeight: '90vh',
        overflow: 'hidden',
      }}
      dialogClassName="p-0 m-0 border-0 shadow-none"
    >
      <Modal.Header closeButton>
        <Modal.Title>Courses</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          padding: '20px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          maxHeight: '70vh',
          overflowY: 'scroll',
          scrollbarWidth: 'none',           // Firefox
          msOverflowStyle: 'none',          // IE 10+
        }}
      >
        <div className="h-100">
          <Form.Group className="mt-2">
            <Form.Label className="fw-bold text-secondary fs-12">Subject</Form.Label>
            <Select
              options={instrument}
              value={instrument.find((inst) => inst.value === course.instrument)}
              onChange={(e) => handleUpdateChanges("instrument", e?.value)}
            />
          </Form.Group>

          <Form.Group className="mt-2">
            <Form.Label className="fw-bold text-secondary fs-12">Title</Form.Label>
            <Form.Control type="text" onChange={(e) => handleUpdateChanges("title", e.target.value)} value={course.title} />
          </Form.Group>

          <Form.Group className="mt-2">
            <Form.Label className="fw-bold text-secondary fs-12">Price Per Class</Form.Label>
            <Form.Control type="number" onChange={(e) => handleUpdateChanges("price_per_class", e.target.value)} value={course.price_per_class} />
          </Form.Group>

          <Form.Group className="mt-3 d-flex">
            <Form.Check checked={course.isVisible} label="Visible" onChange={(e) => handleUpdateChanges("isVisible", e.target.checked)} />
            <Form.Check checked={course.deleted} label="Deleted" className="ms-4" onChange={(e) => handleUpdateChanges("deleted", e.target.checked)} />
            <Form.Check checked={course.approve} label="Approved" className="ms-4" onChange={(e) => handleUpdateChanges("approve", e.target.checked)} />
          </Form.Group>

          <Alert variant="warning" className="mt-3 fs-12">
            <b>Warning:</b> Make sure you are altering discounts correctly
          </Alert>

          <div className="mb-2">
            <AccountNav tab={tab} setTab={setTab} />
          </div>
          <div>
            {tab === "international" &&
              <div>
                <div className="d-flex justify-content-end align-items-center">
                  <IoIosAddCircle className="cursor-pointer text-success" style={{ fontSize: "25px" }} onClick={addDiscount} />
                </div>

                {course.international_discounts.length == 0 ?
                  <div>
                    <Alert variant="warning" className="p-0 px-3 py-2">No international bundles found.</Alert>
                  </div> :
                  <div>
                    {
                      course.international_discounts.map((discount: any, countryIndex: number) => (
                        <Card key={countryIndex} className="mt-3 p-3">
                          <div className="d-flex justify-content-between">
                            <Form.Label>Country</Form.Label>
                            <IoIosRemoveCircle className="text-danger cursor-pointer" style={{ fontSize: "25px" }} onClick={() => removeDiscount(countryIndex)} />
                          </div>

                          <Select
                            options={foreignCountries.map((c) => ({ label: c, value: c }))}
                            value={{ label: discount.country, value: discount.country }}
                            onChange={(selected) => handleCountryChange(countryIndex, selected)}
                          />
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <Form.Label>Discounts</Form.Label>
                            <IoIosAddCircle className="cursor-pointer text-success" style={{ fontSize: "25px" }} onClick={() => addCountryDiscount(countryIndex)} />
                          </div>


                          {discount.discount.length == 0 ?
                            <div>
                              <Alert variant="warning" className="p-0 px-3 py-2">No bundles found for {discount.country}</Alert>
                            </div> :
                            <div>
                              {discount.discount.map((data: any, discountIndex: number) => (
                                <div key={discountIndex} className="bg-light rounded mt-2">
                                  <Form.Group className="mt-2 p-2">
                                    <Row className="align-items-center">
                                      <Col md={2} style={{ fontSize: 12 }}>Buy</Col>
                                      <Col md={3}>
                                        <Form.Control type="number" value={data.num_classes}
                                          onChange={(e) => handleDiscountChange(countryIndex, discountIndex, "num_classes", e.target.value)} />
                                      </Col>
                                      <Col md={2} style={{ fontSize: 12 }}>Get</Col>
                                      <Col md={3}>
                                        <Form.Control type="number" value={data.discount}
                                          onChange={(e) => handleDiscountChange(countryIndex, discountIndex, "discount", e.target.value)} />
                                      </Col>
                                      <Col md={2} style={{ fontSize: 12 }}>% Off</Col>
                                    </Row>
                                  </Form.Group>

                                  <div className="mt-1 d-flex justify-content-end p-2">
                                    <IoIosRemoveCircle className="text-danger" style={{ fontSize: "20px", cursor: "pointer" }}
                                      onClick={() => removeCountryDiscount(countryIndex, discountIndex)} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          }

                        </Card>
                      ))
                    }
                  </div>
                }
              </div>
            }
            {tab == "india" &&
              <div>
                {course.discount.length == 0 ?
                  <Alert variant="warning" className="p-0 px-3 py-2">No India bundles found.</Alert> :
                  <div>
                    <Card className="mt-3 p-3">
                      <div className="d-flex justify-content-between">
                        <Form.Label>Country : <text className="fw-bold">India</text></Form.Label>
                        {/* <IoIosRemoveCircle className="text-danger cursor-pointer" style={{ fontSize: "25px" }} /> */}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <Form.Label>Discounts</Form.Label>
                        <IoIosAddCircle className="cursor-pointer text-success" style={{ fontSize: "25px" }} onClick={() => { addIndiaDiscount() }} />
                      </div>


                      {course.discount.map((data: any, discountIndex: number) => (
                        <div key={discountIndex} className="bg-light rounded mt-2">
                          <Form.Group className="mt-2 p-2">
                            <Row className="align-items-center">
                              <Col md={2} style={{ fontSize: 12 }}>Buy</Col>
                              <Col md={3}>
                                <Form.Control type="number" value={data.num_classes} onChange={(e) => { handleChangeIndiaDiscount(discountIndex, e.target.value, 'num_classes') }} />
                              </Col>
                              <Col md={2} style={{ fontSize: 12 }}>Get</Col>
                              <Col md={3}>
                                <Form.Control type="number" value={data.discount} onChange={(e) => { handleChangeIndiaDiscount(discountIndex, e.target.value, 'discount') }} />
                              </Col>
                              <Col md={2} style={{ fontSize: 12 }}>% Off</Col>
                            </Row>
                          </Form.Group>
                          <div className="mt-1 d-flex justify-content-end p-2">
                            <IoIosRemoveCircle className="text-danger" style={{ fontSize: "20px", cursor: "pointer" }} onClick={() => { handleRemoveIndiaDiscount(discountIndex) }} />
                          </div>
                        </div>
                      ))}

                    </Card>
                  </div>
                }

              </div>
            }
          </div>

          <Button variant="secondary" disabled={loading} className="mt-3" onClick={editCourse}>Update Course</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}


const AccountNav = ({ tab, setTab }: any) => {
  return (
    <div className="col-auto">
      <ul className="nav nav-tabs  header-tabs">
        <li className="nav-item">
          <div className={`nav-link pointer pt-2 pb-2 px-1 cursor-pointer ${tab === 'india' && 'active'}`} onClick={() => setTab('india')}>India</div>
        </li>
        <li className="nav-item">
          <div className={`nav-link pointer pt-2 pb-2 px-1 cursor-pointer ${tab === 'international' && 'active'}`} onClick={() => setTab('international')}>International</div>
        </li>
      </ul>
    </div>
  )
}